import { SkyButton, SkyCard, SkyText } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import type { RunFailure } from "@shared/types/runs";
import { TechnicalDetailsButton } from "./TechnicalDetailsDialog";

/**
 * What went wrong on a failed or partial run (round 10, R2.1), in one card under the outcome
 * callout: the step, the tables, the category, the message and what to do.
 *
 * Every word is the server's (`server/services/runFailureView.ts`: the cleaned error and the
 * cause tables, a bare extractor code already read as its label). This component composes no
 * sentence of its own and never shows a code. A message over `LONG_MESSAGE` characters is cut
 * to that length until the reader asks for all of it.
 */

/** The message length above which "Show full message" appears. */
export const FAILURE_LONG_MESSAGE = 300;

/** How many table names show before "and N more". */
const TABLES_SHOWN = 10;

/** The tables the failure names, the first `TABLES_SHOWN` of them, then "and N more". */
function TablesRow({ failure }: { failure: RunFailure }) {
  const more = failure.tableTotal - failure.tables.length;
  return (
    <Row term={failure.tableTotal === 1 ? "Table" : "Tables"} testId="run-failure-tables">
      <ul className="m-0 list-none p-0">
        {failure.tables.slice(0, TABLES_SHOWN).map((table) => (
          <li key={`${table.dataflowId}:${table.name}`} data-testid="run-failure-table">
            {table.connection ? `${table.connection} / ${table.name}` : table.name}
            {table.label ? (
              <SkyText size={200} color="muted">
                {` · ${table.label}`}
              </SkyText>
            ) : null}
          </li>
        ))}
      </ul>
      {more > 0 ? (
        <SkyText size={200} color="muted" block testId="run-failure-tables-more">
          {`and ${more.toLocaleString("en-US")} more`}
        </SkyText>
      ) : null}
    </Row>
  );
}

export type WhatToDo = NonNullable<RunFailure["whatToDo"]>;

/**
 * #1084: who may take the card's suggested action, decided as the cause block decides
 * (`ImportCauseBlock`): the page's `permitted` over the action's key, and the ask line for a
 * reader without it. Absent, the card names the action as the server sent it.
 */
export interface WhatToDoAccess {
  permitted: (whatToDo: WhatToDo) => boolean;
  /** Absent, the cause's own `withoutPermission` (#1659). */
  askLine?: (whatToDo: WhatToDo) => string | null;
}

/** The line a reader who may not take the action reads, or null. */
function askLineOf(
  whatToDo: WhatToDo,
  permitted: boolean,
  access: WhatToDoAccess | undefined,
): string | null {
  if (permitted || !access) return null;
  if (access.askLine) return access.askLine(whatToDo);
  return whatToDo.withoutPermission ?? null;
}

/** The server's fix, and its suggested action when it names one and the reader may take it. */
function WhatToDoRow({ whatToDo, access }: { whatToDo: WhatToDo; access?: WhatToDoAccess }) {
  const permitted = !access || whatToDo.permission === undefined || access.permitted(whatToDo);
  const ask = askLineOf(whatToDo, permitted, access);
  return (
    <Row term="What to do" testId="run-failure-what-to-do">
      <SkyText size={300} block>
        {whatToDo.fix}
      </SkyText>
      {whatToDo.actionLabel && permitted ? (
        <SkyText size={200} color="muted" block testId="run-failure-suggested-action">
          {`Suggested action: ${whatToDo.actionLabel}`}
        </SkyText>
      ) : null}
      {ask ? (
        <SkyText size={200} color="muted" block testId="run-failure-ask">
          {ask}
        </SkyText>
      ) : null}
    </Row>
  );
}

/** #965: a fix the message already says word for word is not a second thing to do. */
function repeatsMessage(fix: string, message: string | null): boolean {
  return message !== null && message.includes(fix.trim());
}

export function RunFailureCard({
  failure,
  runId,
  access,
}: {
  failure: RunFailure;
  /** #1084: the reader's access to the suggested action; absent, it is named as sent. */
  access?: WhatToDoAccess;
  /** R2.4: with the failure's `technicalEventId`, offers "Technical details" to audit readers. */
  runId?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const message = failure.message;
  const long = message !== null && message.length > FAILURE_LONG_MESSAGE;
  const shown =
    message !== null && long && !expanded ? `${message.slice(0, FAILURE_LONG_MESSAGE)}…` : message;

  return (
    <SkyCard
      appearance="outline"
      className="min-w-0 [overflow-wrap:anywhere]"
      header={{ title: "What went wrong" }}
      testId="run-failure"
    >
      <dl className="grid grid-cols-[minmax(0,1fr)] gap-x-4 gap-y-2 sm:grid-cols-[10rem_minmax(0,1fr)]">
        <Row term="Step" testId="run-failure-step">
          {failure.step}
        </Row>
        {failure.category ? (
          <Row term="Category" testId="run-failure-category">
            {failure.category}
          </Row>
        ) : null}
        {failure.tableTotal > 0 ? <TablesRow failure={failure} /> : null}
        {shown ? (
          <Row term="Message" testId="run-failure-message">
            <SkyText size={300} block className="whitespace-pre-wrap break-words">
              {shown}
            </SkyText>
            {long ? (
              <SkyButton
                type="button"
                appearance="subtle"
                size="small"
                aria-expanded={expanded}
                onClick={() => setExpanded((open) => !open)}
                testId="run-failure-expand"
              >
                {expanded ? "Show less" : "Show full message"}
              </SkyButton>
            ) : null}
          </Row>
        ) : null}
        {failure.whatToDo && !repeatsMessage(failure.whatToDo.fix, failure.message) ? (
          <WhatToDoRow whatToDo={failure.whatToDo} {...(access ? { access } : {})} />
        ) : null}
      </dl>
      {runId && failure.technicalEventId ? (
        <div className="mt-2">
          <TechnicalDetailsButton
            runId={runId}
            eventId={failure.technicalEventId}
            testId="run-failure-technical-details"
          />
        </div>
      ) : null}
    </SkyCard>
  );
}

function Row({
  term,
  testId,
  children,
}: {
  term: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <dt>
        <SkyText size={200} color="muted">
          {term}
        </SkyText>
      </dt>
      <dd className="m-0 min-w-0" data-testid={testId}>
        {children}
      </dd>
    </>
  );
}
